import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Target, Lightbulb, Zap, TrendingUp, CheckCircle2 } from 'lucide-react';
import MarketingLayout from '../../components/Marketing/MarketingLayout';

const AboutUsPage = () => {
  const navigate = useNavigate();

  return (
    <MarketingLayout 
      title="About Us | CRM Automax"
      metaDescription="Learn about CRM Automax — an all-in-one CRM platform built to help businesses and agencies manage leads, automate WhatsApp outreach, and run bulk email campaigns affordably."
    >
      {/* ── Page Header ── */}
      <section className="marketing-hero-section" style={{
        padding: '100px 48px 80px',
        textAlign: 'center',
        background: 'linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%)'
      }}>
        <h1 className="animate-fade-up" style={{ 
          fontSize: 'clamp(40px, 6vw, 56px)', fontWeight: '900', color: '#1e293b', 
          lineHeight: 1.1, letterSpacing: '-0.04em', margin: '0 auto 24px'
        }}>
          About CRM Automax
        </h1>
        <p className="animate-fade-up" style={{ 
          fontSize: '22px', color: '#3b82f6', fontWeight: '600', maxWidth: '700px', 
          margin: '0 auto', lineHeight: 1.6, animationDelay: '0.1s'
        }}>
          Built for businesses that move fast.
        </p>
      </section>

      {/* ── Content Section ── */}
      <section className="marketing-content-section" style={{ padding: '80px 48px', maxWidth: '1000px', margin: '0 auto' }}>
        
        {/* Our Story */}
        <div className="animate-fade-up" style={{ marginBottom: '80px', animationDelay: '0.2s' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
            <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(59, 130, 246, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Lightbulb size={24} color="#3b82f6" />
            </div>
            <h2 style={{ fontSize: '32px', fontWeight: '800', color: '#1e293b', margin: 0 }}>Our Story</h2>
          </div>
          <div style={{ fontSize: '18px', color: '#475569', lineHeight: 1.8, display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <p>
              CRM Automax was built with a simple goal: give businesses and agencies a single platform to manage leads and outreach, without juggling five different tools or paying enterprise-level prices.
            </p>
            <p>
              We saw that most agencies were stuck switching between separate tools for lead storage, lead generation, WhatsApp messaging, and email campaigns. CRM Automax brings all of that together — lead management, bulk lead upload, lead scraping, WhatsApp API and Web integration, and bulk email — in one connected system.
            </p>
          </div>
        </div>

        {/* Our Mission */}
        <div className="animate-fade-up" style={{ marginBottom: '80px', animationDelay: '0.3s' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
            <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(16, 185, 129, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Target size={24} color="#10b981" />
            </div>
            <h2 style={{ fontSize: '32px', fontWeight: '800', color: '#1e293b', margin: 0 }}>Our Mission</h2>
          </div>
          <p style={{ fontSize: '20px', color: '#475569', lineHeight: 1.6, fontWeight: '500', borderLeft: '4px solid #10b981', paddingLeft: '24px' }}>
            To make powerful lead management and outreach automation accessible and affordable for every business — from solo entrepreneurs to large agencies handling thousands of leads.
          </p>
        </div>

        {/* What Sets Us Apart */}
        <div className="animate-fade-up" style={{ marginBottom: '80px', animationDelay: '0.4s' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px' }}>
            <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(245, 158, 11, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Zap size={24} color="#f59e0b" />
            </div>
            <h2 style={{ fontSize: '32px', fontWeight: '800', color: '#1e293b', margin: 0 }}>What Sets Us Apart</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
            {[
              { title: 'All-in-one platform', desc: 'No need for multiple subscriptions across different tools.' },
              { title: 'Flexible lead handling', desc: 'Upload, scrape, or manually manage leads, your choice.' },
              { title: 'Complete WhatsApp coverage', desc: 'Both API and Web, depending on what fits your business.' },
              { title: 'Built for scale', desc: 'Bulk messaging and bulk email designed for high-volume outreach.' },
              { title: 'Affordable pricing', desc: 'Enterprise-level features without the enterprise price tag.' }
            ].map((item, i) => (
              <div key={i} style={{ background: '#f8fafc', padding: '24px', borderRadius: '16px', border: '1px solid #f1f5f9' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <CheckCircle2 size={20} color="#f59e0b" />
                  <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', margin: 0 }}>{item.title}</h3>
                </div>
                <p style={{ fontSize: '15px', color: '#64748b', margin: 0, lineHeight: 1.5 }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Who We Build For */}
        <div className="animate-fade-up" style={{ animationDelay: '0.5s' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
            <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(139, 92, 246, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <TrendingUp size={24} color="#8b5cf6" />
            </div>
            <h2 style={{ fontSize: '32px', fontWeight: '800', color: '#1e293b', margin: 0 }}>Who We Build For</h2>
          </div>
          <p style={{ fontSize: '18px', color: '#475569', lineHeight: 1.8 }}>
            CRM Automax is built for agencies, growing businesses, and sales teams that need to manage high lead volumes and run consistent outreach across WhatsApp and email — without complexity or high costs.
          </p>
        </div>

      </section>

      {/* ── Bottom CTA ── */}
      <section className="marketing-content-section" style={{ padding: '40px 48px 120px', textAlign: 'center' }}>
        <div style={{ 
          maxWidth: '1000px', margin: '0 auto', background: 'linear-gradient(135deg, #1e293b, #0f172a)', 
          padding: '80px 40px', borderRadius: '40px', color: 'white', position: 'relative', overflow: 'hidden'
        }}>
          <h2 style={{ fontSize: '36px', fontWeight: '800', marginBottom: '20px', position: 'relative', zIndex: 1 }}>
            Join the businesses already simplifying their lead management with CRM Automax.
          </h2>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', position: 'relative', zIndex: 1, marginTop: '40px', flexWrap: 'wrap' }}>
            <button onClick={() => navigate('/pricing')} style={{
              background: '#3b82f6', color: 'white', border: 'none',
              padding: '16px 32px', borderRadius: '12px', fontWeight: '700', fontSize: '16px', cursor: 'pointer',
              boxShadow: '0 10px 15px -3px rgba(59, 130, 246, 0.3)'
            }}>
              View Pricing
            </button>
            <button onClick={() => navigate('/register')} style={{
              background: 'transparent', color: 'white', border: '1px solid rgba(255,255,255,0.3)',
              padding: '16px 32px', borderRadius: '12px', fontWeight: '700', fontSize: '16px', cursor: 'pointer',
              transition: 'background 0.2s ease'
            }} onMouseOver={(e) => e.target.style.background = 'rgba(255,255,255,0.1)'} onMouseOut={(e) => e.target.style.background = 'transparent'}>
              Get Started Today
            </button>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
};

export default AboutUsPage;
